import bindAll from 'lodash.bindall';
import defaultsDeep from 'lodash.defaultsdeep';
import omit from 'lodash.omit';
import PropTypes from 'prop-types';
import React from 'react';
import {injectIntl} from 'react-intl';
import intlShape from '../lib/intlShape.js';

import {connect} from 'react-redux';
import {openBackdropLibrary} from '../reducers/modals';
import {activateTab, COSTUMES_TAB_INDEX} from '../reducers/editor-tab';
import {showStandardAlert, closeAlertWithId} from '../reducers/alerts';
import {setHoveredSprite} from '../reducers/hovered-target';
import DragConstants from '../lib/drag-constants';
import DropAreaHOC from '../lib/drop-area-hoc.jsx';
import ThrottledPropertyHOC from '../lib/throttled-property-hoc.jsx';
import {emptyCostume} from '../lib/empty-assets';
import sharedMessages from '../lib/shared-messages';
import {fetchCode} from '../lib/backpack-api';
import {getEventXY} from '../lib/touch-utils';

import StageSelectorComponent from '../components/stage-selector/stage-selector.jsx';

import backdropLibraryContent from '../lib/libraries/backdrops.json';
import {handleFileUpload, costumeUpload} from '../lib/file-uploader.js';
import {ModalFocusContext} from '../contexts/modal-focus-context.jsx';

import {costumeShape as backdropShape} from '../lib/assets-prop-types.js';
import mergeDynamicAssets from '../lib/merge-dynamic-assets.js';

const dragTypes = [
    DragConstants.COSTUME,
    DragConstants.SOUND,
    DragConstants.BACKPACK_COSTUME,
    DragConstants.BACKPACK_SOUND,
    DragConstants.BACKPACK_CODE
];

const DroppableThrottledStage = DropAreaHOC(dragTypes)(
    ThrottledPropertyHOC('url', 500)(StageSelectorComponent)
);

class StageSelector extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClick',
            'handleNewBackdrop',
            'handleNewBackdropClick',
            'handleSurpriseBackdrop',
            'handleEmptyBackdrop',
            'addBackdropFromLibraryItem',
            'handleFileUploadClick',
            'handleBackdropUpload',
            'handleAIBackdrop',
            'handlePointerEnter',
            'handlePointerLeave',
            'handleTouchEnd',
            'handleDrop',
            'setFileInput',
            'setRef',
            'mergeDynamicAssets'
        ]);

        this.processedBackdrops = {};
    }

    componentDidMount () {
        document.addEventListener('touchend', this.handleTouchEnd);
    }

    componentWillUnmount () {
        document.removeEventListener('touchend', this.handleTouchEnd);
    }

    static contextType = ModalFocusContext;

    mergeDynamicAssets () {
        if (this.processedBackdrops.source === this.props.dynamicBackdrops) {
            return this.processedBackdrops.data;
        }
        this.processedBackdrops = mergeDynamicAssets(
            backdropLibraryContent,
            this.props.dynamicBackdrops
        );
        return this.processedBackdrops.data;
    }

    handleTouchEnd (e) {
        const {x, y} = getEventXY(e);
        const {top, left, bottom, right} = this.ref.getBoundingClientRect();
        if (x >= left && x <= right && y >= top && y <= bottom) {
            this.handlePointerEnter();
        }
    }

    addBackdropFromLibraryItem (item, shouldActivateTab = true) {
        const vmBackdrop = {
            name: item.name,
            md5: item.md5ext,
            rotationCenterX: item.rotationCenterX,
            rotationCenterY: item.rotationCenterY,
            bitmapResolution: item.bitmapResolution,
            skinId: null
        };
        this.handleNewBackdrop(vmBackdrop, shouldActivateTab);
    }

    handleClick () {
        this.props.onSelect(this.props.id);
    }

    handleNewBackdropClick (e) {
        e.stopPropagation();
        this.context.captureFocus();
        this.props.onNewBackdropClick(jsonStr => {
            this.handleNewBackdrop(JSON.parse(jsonStr), false);
        });
    }

    handleNewBackdrop (backdrops_, shouldActivateTab = true) {
        const backdrops = Array.isArray(backdrops_) ? backdrops_ : [backdrops_];

        return Promise.all(backdrops.map(backdrop =>
            this.props.vm.addBackdrop(backdrop.md5, backdrop)
        )).then(() => {
            if (shouldActivateTab) {
                return this.props.onActivateTab(COSTUMES_TAB_INDEX);
            }
        });
    }

    handleSurpriseBackdrop (e) {
        e.stopPropagation();

        const backdrops = this.mergeDynamicAssets();
        const item = backdrops[Math.floor(Math.random() * backdrops.length)];

        this.addBackdropFromLibraryItem(item, false);
    }

    handleEmptyBackdrop (e) {
        e.stopPropagation();

        this.props.vm.setEditingTarget(this.props.id);

        this.handleNewBackdrop(
            emptyCostume(
                this.props.intl.formatMessage(
                    sharedMessages.backdrop,
                    {index: 1}
                )
            )
        );
    }

    handleBackdropUpload (e) {
        const storage = this.props.vm.runtime.storage;

        this.props.onShowImporting();

        handleFileUpload(
            e.target,
            (buffer, fileType, fileName, fileIndex, fileCount) => {
                costumeUpload(
                    buffer,
                    fileType,
                    storage,
                    vmCostumes => {
                        this.props.vm.setEditingTarget(this.props.id);

                        vmCostumes.forEach((costume, i) => {
                            costume.name = `${fileName}${i ? i + 1 : ''}`;
                        });

                        this.handleNewBackdrop(vmCostumes).then(() => {
                            if (fileIndex === fileCount - 1) {
                                this.props.onCloseImporting();
                            }
                        });
                    },
                    this.props.onCloseImporting
                );
            },
            this.props.onCloseImporting
        );
    }

   handleAIBackdrop (imageBase64) {
    console.log('[AI BACKDROP] handleAIBackdrop started', {
        base64Length: imageBase64 && imageBase64.length,
        targetId: this.props.id
    });

    const storage = this.props.vm.runtime.storage;

    this.props.onShowImporting();

    try {
        const cleanBase64 = imageBase64.replace(
            /^data:image\/[a-zA-Z0-9.+-]+;base64,/,
            ''
        );

        console.log('[AI BACKDROP] base64 cleaned', {
            cleanLength: cleanBase64.length
        });

        const image = new Image();

        image.onload = () => {
            console.log('[AI BACKDROP] generated image loaded', {
                width: image.width,
                height: image.height
            });

            const canvas = document.createElement('canvas');
            canvas.width = 480;
            canvas.height = 360;

            const context = canvas.getContext('2d');

            const sourceAspect = image.width / image.height;
            const targetAspect = 480 / 360;

            let sourceX = 0;
            let sourceY = 0;
            let sourceWidth = image.width;
            let sourceHeight = image.height;

            if (sourceAspect > targetAspect) {
                sourceWidth = image.height * targetAspect;
                sourceX = (image.width - sourceWidth) / 2;
            } else if (sourceAspect < targetAspect) {
                sourceHeight = image.width / targetAspect;
                sourceY = (image.height - sourceHeight) / 2;
            }

            console.log('[AI BACKDROP] cropping image to 4:3', {
                sourceX,
                sourceY,
                sourceWidth,
                sourceHeight
            });

            context.drawImage(
                image,
                sourceX,
                sourceY,
                sourceWidth,
                sourceHeight,
                0,
                0,
                480,
                360
            );

            canvas.toBlob(blob => {
                if (!blob) {
                    console.error(
                        '[AI BACKDROP] failed to create resized PNG blob'
                    );
                    this.props.onCloseImporting();
                    return;
                }

                blob.arrayBuffer().then(buffer => {
                    console.log('[AI BACKDROP] resized PNG ready', {
                        byteLength: buffer.byteLength
                    });

                    costumeUpload(
                        buffer,
                        'image/png',
                        storage,
                        vmCostumes => {
                            console.log('[AI BACKDROP] costumeUpload callback', {
                                costumeCount: vmCostumes && vmCostumes.length,
                                vmCostumes
                            });

                            this.props.vm.setEditingTarget(this.props.id);

                            vmCostumes.forEach((costume, i) => {
                                costume.name = `AI Backdrop${i ? ` ${i + 1}` : ''}`;
                            });

                            console.log(
                                '[AI BACKDROP] calling handleNewBackdrop'
                            );

                            this.handleNewBackdrop(vmCostumes).then(() => {
                                console.log(
                                    '[AI BACKDROP] backdrop added successfully'
                                );
                                this.props.onCloseImporting();
                            }).catch(error => {
                                console.error(
                                    '[AI BACKDROP] handleNewBackdrop failed:',
                                    error
                                );
                                this.props.onCloseImporting();
                            });
                        },
                        error => {
                            console.error(
                                '[AI BACKDROP] costumeUpload failed:',
                                error
                            );
                            this.props.onCloseImporting();
                        }
                    );
                }).catch(error => {
                    console.error(
                        '[AI BACKDROP] blob arrayBuffer failed:',
                        error
                    );
                    this.props.onCloseImporting();
                });
            }, 'image/png');
        };

        image.onerror = error => {
            console.error(
                '[AI BACKDROP] failed to load generated image:',
                error
            );
            this.props.onCloseImporting();
        };

        image.src = `data:image/png;base64,${cleanBase64}`;
    } catch (error) {
        console.error(
            '[AI BACKDROP] decode/import failed:',
            error
        );
        this.props.onCloseImporting();
    }
}

    handleFileUploadClick (e) {
        e.stopPropagation();
        this.fileInput.click();
    }

    handlePointerEnter () {
        this.props.dispatchSetHoveredSprite(this.props.id);
    }

    handlePointerLeave () {
        this.props.dispatchSetHoveredSprite(null);
    }

    handleDrop (dragInfo) {
        if (dragInfo.dragType === DragConstants.COSTUME) {
            this.props.vm.shareCostumeToTarget(
                dragInfo.index,
                this.props.id
            );
        } else if (dragInfo.dragType === DragConstants.SOUND) {
            this.props.vm.shareSoundToTarget(
                dragInfo.index,
                this.props.id
            );
        } else if (dragInfo.dragType === DragConstants.BACKPACK_COSTUME) {
            this.props.vm.addCostume(
                dragInfo.payload.body,
                {
                    name: dragInfo.payload.name
                },
                this.props.id
            );
        } else if (dragInfo.dragType === DragConstants.BACKPACK_SOUND) {
            this.props.vm.addSound(
                {
                    md5: dragInfo.payload.body,
                    name: dragInfo.payload.name
                },
                this.props.id
            );
        } else if (dragInfo.dragType === DragConstants.BACKPACK_CODE) {
            fetchCode(dragInfo.payload.bodyUrl)
                .then(blocks => {
                    this.props.vm.shareBlocksToTarget(
                        blocks,
                        this.props.id
                    );
                    this.props.vm.refreshWorkspace();
                });
        }
    }

    setFileInput (input) {
        this.fileInput = input;
    }

    setRef (ref) {
        this.ref = ref;
    }

    render () {
        const componentProps = omit(this.props, [
            'asset',
            'dispatchSetHoveredSprite',
            'id',
            'intl',
            'onNewBackdropClick',
            'onActivateTab',
            'onSelect',
            'onShowImporting',
            'onCloseImporting'
        ]);

        return (
            <DroppableThrottledStage
                componentRef={this.setRef}
                fileInputRef={this.setFileInput}
                onAIBackdropAdd={this.handleAIBackdrop}
                onBackdropFileUpload={this.handleBackdropUpload}
                onBackdropFileUploadClick={this.handleFileUploadClick}
                onClick={this.handleClick}
                onDrop={this.handleDrop}
                onEmptyBackdropClick={this.handleEmptyBackdrop}
                onPointerEnter={this.handlePointerEnter}
                onPointerLeave={this.handlePointerLeave}
                onSurpriseBackdropClick={this.handleSurpriseBackdrop}
                onNewBackdropClick={this.handleNewBackdropClick}
                {...componentProps}
            />
        );
    }
}
StageSelector.propTypes = {
    ...StageSelectorComponent.propTypes,
    id: PropTypes.string,
    intl: intlShape.isRequired,
    onAIBackdropAdd: PropTypes.func,
    onCloseImporting: PropTypes.func,
    onSelect: PropTypes.func,
    onShowImporting: PropTypes.func,
    dynamicBackdrops: PropTypes.arrayOf(backdropShape)
};

const mapStateToProps = (state, {asset, id}) => ({
    url: asset && asset.encodeDataURI(),
    vm: state.scratchGui.vm,
    receivedBlocks: state.scratchGui.hoveredTarget.receivedBlocks &&
            state.scratchGui.hoveredTarget.sprite === id,
    raised: state.scratchGui.blockDrag,
    dynamicBackdrops: state.scratchGui.dynamicAssets.backdrops
});

const mapDispatchToProps = dispatch => ({
    onNewBackdropClick: () => {
        dispatch(openBackdropLibrary());
    },
    onActivateTab: tabIndex => {
        dispatch(activateTab(tabIndex));
    },
    dispatchSetHoveredSprite: spriteId => {
        dispatch(setHoveredSprite(spriteId));
    },
    onCloseImporting: () => dispatch(closeAlertWithId('importingAsset')),
    onShowImporting: () => dispatch(showStandardAlert('importingAsset'))
});

const mergeProps = (stateProps, dispatchProps, ownProps) => defaultsDeep(
    {},
    ownProps,
    stateProps,
    dispatchProps
);

export default injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(StageSelector));